/**
 * KpiCard Component
 *
 * Displays a KPI metric with label, colored icon box, numeric value,
 * and optional percentage badge.
 *
 * @module pages/DashboardPage/components/KpiCard
 */

import type { CSSProperties } from 'react';
import { memo, useMemo } from 'react';

import clsx from 'clsx';

import type { KpiCardProps } from './KpiCard.types';
import styles from './KpiCard.module.css';

/**
 * KpiCard Component
 *
 * @example
 * ```tsx
 * <KpiCard
 *   label="In Progress"
 *   value={12}
 *   total={50}
 *   icon={<IconProgress size={18} />}
 *   color="blue"
 * />
 * ```
 */
export const KpiCard = memo(function KpiCard({
  label,
  value,
  total,
  icon,
  color,
  compact = false,
}: KpiCardProps) {
  const colorStyle = useMemo(
    () =>
      ({
        '--kpi-color': `var(--mantine-color-${color}-6)`,
        '--kpi-color-light': `var(--mantine-color-${color}-1)`,
      }) as CSSProperties,
    [color]
  );

  const percentage = total > 0 ? Math.round((value / total) * 100) : 0;

  return (
    <div
      className={clsx(styles.kpiCard, compact && styles.compact)}
      style={colorStyle}
    >
      <div className={styles.topRow}>
        <span className={styles.label}>{label}</span>
        <div className={styles.iconBox}>{icon}</div>
      </div>
      <div className={styles.bottomRow}>
        <span className={styles.value}>{value}</span>
        {value > 0 && <span className={styles.badge}>{percentage}%</span>}
      </div>
    </div>
  );
});

KpiCard.displayName = 'KpiCard';
